(ns hai.tags.page
  "Tags page — a table of the tags you own, with sharing (read-only) to others.
   A tag is a `:group.type/tag`; sharing rides the same popup as connections via
   the polymorphic backend in `hai.pathom.sharing`."
  (:require
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.chip :as chip-ui]
   [hai.ui.components.tags :as tag-ui]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table-views]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private share-icon (icons/mui-icon :outlined :person_add))
(def ^:private trash-icon (icons/mui-icon :outlined :delete))

(defn- can-delete? [tag]
  (some #{:permission.type/admin} (:user.entity/permissions tag)))

(o/defstyled tag-card card/card
  :cursor-default
  ;; The luggage-tag chip is content-width; centre it in the card rather than
  ;; letting it hug the left edge (auto block margins centre it vertically too).
  [chip-ui/entity-chip {:align-self "center"
                        :margin-top "auto"
                        :margin-bottom "auto"}]
  ;; Narrow cards can't fit the prompt and buttons on one row, so stack them.
  [:.confirm :flex :flex-col
   {:gap "8px"
    :margin-top "10px"
    :padding-top "10px"
    :border-top-width "1px"
    :border-top-style "solid"
    :border-top-color t/--card-forest-14
    :font-size "13px"
    :color t/--card-forest}]
  [:.confirm-prompt {:color t/--link-muted :text-align "center"}]
  [:.confirm-actions :flex :items-center
   {:gap "8px" :justify-content "center"}]
  [:.delete-yes
   {:padding "6px 14px"
    :border "none" :border-radius "8px"
    :background t/--property-13 :color "white"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--property-14}]]
  [:.delete-no
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :font-family "inherit"
    :font-size "13px" :color t/--link-muted
    :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

(defn- share-dialog [!open? collection tag]
  [c/DialogRoot
   {:open @!open?
    :onOpenChange (fn [open?]
                    (reset! !open? open?)
                    (when-not open?
                      (>e [:table/resolve collection])))}
   [c/DialogContent
    [sharing.components/sharing-dialog-title]
    [c/dialog-close-x]
    [c/DialogDescription "People with access to this tag's notes and contacts"]
    [sharing.components/permissions-table tag]]])

(defn- card-menu [!share-open? !confirming? collection tag]
  (let [actions (cond-> []
                  (sharing/can-edit? tag) (conj ["Share" (fn [_] (reset! !share-open? true)) share-icon])
                  (can-delete? tag)       (conj ["Delete" (fn [_] (reset! !confirming? true)) trash-icon]))]
    (when (seq actions)
      [:span {:on-click #(.stopPropagation %)}
       [share-dialog !share-open? collection tag]
       [table-views/more-menu-cell* actions]])))

(defn- delete-confirm [!confirming? collection tag]
  [:div.confirm
   [:span.confirm-prompt "Delete this tag?"]
   [:div.confirm-actions
    [:button.delete-no
     {:type "button"
      :on-click (fn [e]
                  (.stopPropagation e)
                  (reset! !confirming? false))}
     "Cancel"]
    [:button.delete-yes
     {:type "button"
      :on-click (fn [e]
                  (.stopPropagation e)
                  (reset! !confirming? false)
                  (>e [:resolve [(list 'tag/delete {:group/uuid (:group/uuid tag)})]
                       {:on-success-fx [[:dispatch [:table/resolve collection]]]}]))}
     "Delete"]]])

(o/defstyled narrow-card-grid :div
  ;; Tag cards hold only a chip, so tighten the shared card grid's columns —
  ;; otherwise each card stretches to solution-grid's 280px minimum.
  [card/solution-grid
   {:grid-template-columns "repeat(auto-fill, minmax(180px, 1fr))"}])

(defn tag-card-render
  [collection {tag :form/current-value :as row}]
  (r/with-let [!share-open? (r/atom false)
               !confirming? (r/atom false)]
    [tag-card
     [card/card-top-row
      [cards/card-select-checkbox collection row]
      [card-menu !share-open? !confirming? collection tag]]
     [tag-ui/chip {:label (tag-ui/with-hash (:group/display-name tag))}]
     (when @!confirming?
       [delete-confirm !confirming? collection tag])]))

(defn tags-table []
  [collection/component
   {:id ::table
    :query [{:user.tags/table
             [{:collection/items
               [:group/uuid
                :group/display-name
                :group/type
                :user.entity/permissions
                sharing.components/shared-with-query]}]}]
    :paginate? true
    :default-view :collection.view/table
    :no-items (fn [_] [c/Text [:em "You haven't created any tags yet. Add one when writing a note."]])
    :card tag-card-render
    :columns [{:header "Tag"
               :attr :group/display-name
               :render (fn [{:group/keys [display-name]}]
                         [c/TableCell [tag-ui/chip {:label (tag-ui/with-hash display-name)}]])}
              sharing.components/shared-with-column]
    :bulk-actions [{:title "Delete"
                    :disabled? bulk-actions/disabled?
                    :mutation 'tag/delete
                    :alert {:content (fn [{:table/keys [selected] :as table*} action]
                                       [bulk-actions/confirm-dialog
                                        {:table  table*
                                         :action action}
                                        [bulk-actions/title "Delete tags"]
                                        [bulk-actions/description "Are you sure you want to delete the following tags?"]
                                        (into [:ul]
                                              (map (fn [{:group/keys [display-name]}]
                                                     [:li [:strong (tag-ui/with-hash display-name)]]))
                                              selected)])}}]}])

(defn page [_route]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/content
    [narrow-card-grid
     [tags-table]]]])
